import { useId, type ComponentType, type SVGProps } from "react";
import type { BadgeSummary, CommunityBadge, UserBadge, UserSummary } from "@atrium/shared";
import { cn } from "../lib/cn";
import { joinedDate } from "../lib/format";
import { Tooltip } from "./ui/Tooltip";
import {
  IconBadgeBolt,
  IconBadgeBot,
  IconBadgeCalendar,
  IconBadgeCheck,
  IconBadgeBug,
  IconBadgeCode,
  IconBadgeCodeCheck,
  IconBadgeCrown,
  IconBadgeFire,
  IconBadgeFlag,
  IconBadgeHammer,
  IconBadgeHeart,
  IconBadgeHouse,
  IconBadgeLeaf,
  IconBadgeShield,
  IconBadgeSparkle,
  IconBadgeStar,
  IconBadgeTrophy,
} from "./socialIcons";

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

/** BADGE_ICONS → seal glyphs. Unknown names fall back to a star. */
export const BADGE_ICON_MAP: Record<string, Icon> = {
  star: IconBadgeStar,
  shield: IconBadgeShield,
  check: IconBadgeCheck,
  code: IconBadgeCode,
  code_check: IconBadgeCodeCheck,
  bot: IconBadgeBot,
  bug: IconBadgeBug,
  house: IconBadgeHouse,
  heart: IconBadgeHeart,
  flag: IconBadgeFlag,
  crown: IconBadgeCrown,
  hammer: IconBadgeHammer,
  sparkle: IconBadgeSparkle,
  calendar: IconBadgeCalendar,
  trophy: IconBadgeTrophy,
  fire: IconBadgeFire,
  leaf: IconBadgeLeaf,
  bolt: IconBadgeBolt,
};

const RARITY: Record<string, string> = { common: "Common", uncommon: "Uncommon", rare: "Rare", epic: "Epic", legendary: "Legendary" };

function BadgeTip({ badge }: { badge: BadgeSummary | UserBadge }) {
  const awarded = "awardedAt" in badge ? badge.awardedAt : null;
  return (
    <span className="block max-w-56 py-0.5">
      <span className="block font-semibold">{badge.name}</span>
      {badge.description && <span className="mt-0.5 block font-normal text-fg-2">{badge.description}</span>}
      <span className="mt-0.5 block font-normal text-fg-3">
        {RARITY[badge.rarity] ?? badge.rarity}
        {awarded && ` · Since ${joinedDate(awarded)}`}
      </span>
    </span>
  );
}

/** Scalloped seal behind every badge: a circle with ten soft bumps, in a 24x24 box. */
const SEAL_PATH = (() => {
  const pts = Array.from({ length: 160 }, (_, i) => {
    const t = (i / 160) * Math.PI * 2;
    const r = 10.6 + 0.85 * Math.cos(10 * t);
    return `${(12 + r * Math.sin(t)).toFixed(2)} ${(12 - r * Math.cos(t)).toFixed(2)}`;
  });
  return `M${pts.join("L")}Z`;
})();

const SEAL_SIZE = { sm: "size-[18px]", md: "size-5", lg: "size-7" } as const;
type SealSize = keyof typeof SEAL_SIZE;

/** A badge mark: a white glyph on a seal of the badge colour, with a soft sheen across the top. */
export function Seal({ icon: I, color, size = "sm", className }: { icon: Icon; color: string; size?: SealSize; className?: string }) {
  const sheen = useId();
  return (
    <span className={cn("relative inline-flex shrink-0 items-center justify-center", SEAL_SIZE[size], className)}>
      <svg viewBox="0 0 24 24" className="absolute inset-0 size-full" aria-hidden>
        <defs>
          <linearGradient id={sheen} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#fff" stopOpacity="0.28" />
            <stop offset="0.55" stopColor="#fff" stopOpacity="0" />
            <stop offset="1" stopColor="#000" stopOpacity="0.12" />
          </linearGradient>
        </defs>
        <path d={SEAL_PATH} fill={color} />
        <path d={SEAL_PATH} fill={`url(#${sheen})`} />
      </svg>
      <I className="relative size-[60%] text-white drop-shadow-[0_0.5px_0.5px_rgb(0_0_0/0.25)]" aria-hidden />
    </span>
  );
}

/** One badge: uploaded icon if there is one, otherwise the built-in glyph on a seal in the badge colour. */
export function BadgeIcon({ badge, size = "sm", tooltip = true, className }: { badge: BadgeSummary | UserBadge; size?: SealSize; tooltip?: boolean; className?: string }) {
  const body = (
    <span role="img" aria-label={badge.name} tabIndex={tooltip ? 0 : undefined} className={cn("user-badge inline-flex shrink-0 items-center justify-center rounded-full focus-visible:outline-2", className)}>
      {badge.iconUrl ? (
        <img src={badge.iconUrl} alt="" className={cn(SEAL_SIZE[size], "object-contain")} />
      ) : (
        <Seal icon={BADGE_ICON_MAP[badge.icon] ?? IconBadgeStar} color={badge.color} size={size} />
      )}
    </span>
  );
  return tooltip ? <Tooltip label={<BadgeTip badge={badge} />}>{body}</Tooltip> : body;
}

/** Small "Bot" tag for automated accounts. */
export function BotTag({ className }: { className?: string }) {
  return (
    <Tooltip label="Automated account">
      <span tabIndex={0} className={cn("inline-flex h-[18px] shrink-0 items-center rounded bg-accent-soft px-1 text-[11px] font-semibold uppercase leading-none text-accent", className)}>
        Bot
      </span>
    </Tooltip>
  );
}

/** Orange seal shared by the staff mark and the "Atrium staff" badge. */
export const STAFF_COLOR = "#f97316";

/** Shield marking official Atrium staff. It comes from the account's platform role, so it can't be hidden or earned. */
export function StaffShield({ size = "sm", className }: { size?: SealSize; className?: string }) {
  return (
    <Tooltip
      label={
        <span className="block max-w-56 py-0.5">
          <span className="block font-semibold">Atrium staff</span>
          <span className="mt-0.5 block font-normal text-fg-2">Works on Atrium. Staff will never ask for your password.</span>
        </span>
      }
    >
      <span role="img" aria-label="Atrium staff" tabIndex={0} className={cn("inline-flex shrink-0 items-center justify-center rounded-full focus-visible:outline-2", className)}>
        <Seal icon={IconBadgeShield} color={STAFF_COLOR} size={size} />
      </span>
    </Tooltip>
  );
}

/** Identity marks that sit right after a name: the staff shield and the Bot tag. */
export function NameTags({ user, size = "sm", className }: { user: Pick<UserSummary, "bot" | "staff">; size?: "sm" | "md" | "lg"; className?: string }) {
  if (!user.bot && !user.staff) return null;
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1", className)} onClick={(e) => e.stopPropagation()}>
      {user.staff && <StaffShield size={size} />}
      {user.bot && <BotTag />}
    </span>
  );
}

/**
 * Compact badge row shown next to a name: the staff shield, the user's leading
 * badge by default (at most `max`), plus a Bot tag for bot accounts. The full set lives on the profile.
 */
export function UserBadges({ user, max = 1, size = "sm", className }: { user: Pick<UserSummary, "badges" | "bot" | "staff">; max?: number; size?: "sm" | "md"; className?: string }) {
  const badges = (user.badges ?? []).slice(0, max);
  if (!badges.length && !user.bot && !user.staff) return null;
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1", className)} onClick={(e) => e.stopPropagation()}>
      {user.staff && <StaffShield size={size} />}
      {user.bot && <BotTag />}
      {badges.map((b) => (
        <BadgeIcon key={b.id} badge={b} size={size} />
      ))}
    </span>
  );
}

/** How each staff mark on a community looks. Order matches COMMUNITY_BADGES. */
export const COMMUNITY_BADGE_INFO: Record<CommunityBadge, { name: string; description: string; color: string; icon: Icon }> = {
  official: { name: "Official", description: "Run by the Atrium team.", color: "#22c55e", icon: IconBadgeCheck },
  partner: { name: "Partner", description: "An Atrium partner community.", color: "#f43f5e", icon: IconBadgeHouse },
  featured: { name: "Featured", description: "Picked by Atrium staff.", color: "#8b5cf6", icon: IconBadgeStar },
};

/** Official, partner and featured seals after a community's name. */
export function CommunityBadges({ badges, size = "sm", className }: { badges: CommunityBadge[] | undefined; size?: SealSize; className?: string }) {
  if (!badges?.length) return null;
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1", className)}>
      {badges.map((b) => {
        const info = COMMUNITY_BADGE_INFO[b];
        return (
          <Tooltip
            key={b}
            label={
              <span className="block max-w-56 py-0.5">
                <span className="block font-semibold">{info.name}</span>
                <span className="mt-0.5 block font-normal text-fg-2">{info.description}</span>
              </span>
            }
          >
            <span role="img" aria-label={info.name} tabIndex={0} className="inline-flex shrink-0 rounded-full focus-visible:outline-2">
              <Seal icon={info.icon} color={info.color} size={size} />
            </span>
          </Tooltip>
        );
      })}
    </span>
  );
}
